import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Plus,
  Wrench,
  Package,
  History,
  Trash2,
  Edit3,
  CheckCircle2,
  AlertTriangle,
  Search,
  Building2,
  ArrowRight,
  FileText,
  Tags,
  Boxes,
  Upload,
  Loader2,
  Download,
  PackageCheck,
  Calculator,
} from "lucide-react";
import api from "../lib/api";
import { toast } from "../components/ui/sonner";
import Modal from "../components/Modal";
import DateTimePicker from "../components/DateTimePicker";
import SearchableSelect from "../components/SearchableSelect";
import { useAuth } from "../context/AuthContext";
import { usePersistedState } from "../lib/persisted";
import DatasheetsTab from "../components/equipment/DatasheetsTab";
// Iter 402.0 — ProcurementPlanner deleted; replaced by the new
// Procurement Tracker at /procurement (mirror of picking lists).
import BulkImportModal from "../components/BulkImportModal";
import StockMovementsModal from "../components/StockMovementsModal";
import VoiceTextarea from "../components/VoiceTextarea";

const CATEGORIES = [
  { v: "filter", label: "Filter", color: "#1FB6C1" },
  { v: "membrane", label: "Membrane", color: "#A78BFA" },
  { v: "pump", label: "Pump", color: "#F59E0B" },
  { v: "chemical", label: "Chemical", color: "#10B981" },
  { v: "consumable", label: "Consumable", color: "#22C55E" },
  { v: "tank", label: "Tank", color: "#3B82F6" },
  { v: "component", label: "Component", color: "#EAB308" },
  { v: "general", label: "General", color: "#647087" },
];

const UNITS = ["pcs", "kg", "l", "m", "bag"];

export default function Equipment() {
  const { user } = useAuth();
  const isManager = user && (user.role === "owner" || user.role === "admin" || user.role === "manager");
  const [tab, setTab] = usePersistedState("equipment.tab", "inventory"); // inventory | procurement | datasheets

  const [inventory, setInventory] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [sites, setSites] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [dsCounts, setDsCounts] = useState({});

  const [invModal, setInvModal] = useState(null);  // { editing? }
  const [stockModal, setStockModal] = useState(null);  // inventory row for stock-history modal
  const [eqModal, setEqModal] = useState(null);    // { editing? } — opened by external nav (kept for re-entry)
  const [serviceModal, setServiceModal] = useState(null);

  const [inventoryLoading, setInventoryLoading] = useState(false);
  const [inventoryProgress, setInventoryProgress] = useState({ loaded: 0, total: 0 });

  const loadInventoryChunked = async () => {
    // Stream inventory in 1k-row pages so the ~17 k-row production
    // collection doesn't slam into the 60 s ingress timeout that
    // happens when /api/inventory tries to return everything in one shot.
    // First page paints instantly; subsequent pages accumulate.
    setInventoryLoading(true);
    try {
      const PAGE = 1000;
      let skip = 0;
      let collected = [];
      // First page
      const first = await api.get(`/inventory?paginated=true&limit=${PAGE}&skip=0`);
      const firstData = first?.data || {};
      collected = Array.isArray(firstData.items) ? firstData.items : [];
      const total = Number(firstData.total) || collected.length;
      setInventory(collected);
      setInventoryProgress({ loaded: collected.length, total });
      // Subsequent pages — accumulate
      while (collected.length < total) {
        skip = collected.length;
        const next = await api.get(`/inventory?paginated=true&limit=${PAGE}&skip=${skip}`);
        const items = Array.isArray(next?.data?.items) ? next.data.items : [];
        if (!items.length) break;
        collected = collected.concat(items);
        setInventory(collected.slice());
        setInventoryProgress({ loaded: collected.length, total });
      }
    } catch (e) {
      // Keep whatever we managed to load — don't blank the page.
    } finally {
      setInventoryLoading(false);
    }
  };

  const load = async () => {
    const [e, c, s, sup, ds] = await Promise.all([
      api.get("/equipment").catch(() => ({ data: [] })),
      api.get("/customers").catch(() => ({ data: [] })),
      api.get("/sites").catch(() => ({ data: [] })),
      api.get("/suppliers").catch(() => ({ data: { items: [] } })),
      api.get("/inventory/datasheet-counts").catch(() => ({ data: {} })),
    ]);
    const asArray = (v) => (Array.isArray(v) ? v : Array.isArray(v?.items) ? v.items : []);
    setEquipment(asArray(e.data));
    setCustomers(asArray(c.data));
    setSites(asArray(s.data));
    setSuppliers(asArray(sup.data?.items ?? sup.data));
    setDsCounts((ds.data && typeof ds.data === "object" && !Array.isArray(ds.data)) ? ds.data : {});
    // Inventory is loaded separately, paginated, so the 17 k-row collection
    // doesn't time out at the production ingress.
    loadInventoryChunked();
  };
  useEffect(() => {
    load();
  }, []);

  const customerName = (id) => customers.find((c) => c.id === id)?.name || "—";
  const siteName = (id) => sites.find((s) => s.id === id)?.name || "—";
  const itemById = (id) => inventory.find((i) => i.id === id);

  return (
    <div className="space-y-5" data-testid="equipment-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-[#647087]">Stockroom</div>
          <h1 className="font-display text-2xl md:text-4xl font-bold tracking-tight mt-1 flex items-center gap-3">
            <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
            Inventory
          </h1>
        </div>
        {/* Wave 30 Chunk 11e — surface Picking + Estimator from the
            Inventory page so users don't have to dig through Tools or
            Sales to reach the inventory-driven AI workflows. */}
        <div className="flex items-center gap-2 flex-wrap">
          <Link
            to="/picking"
            className="hv-btn hv-btn-ghost text-xs"
            data-testid="equipment-picking-link"
          >
            <PackageCheck className="w-3 h-3" /> Picking lists
          </Link>
          <Link
            to="/estimator"
            className="hv-btn hv-btn-ghost text-xs"
            data-testid="equipment-estimator-link"
          >
            <Calculator className="w-3 h-3" /> Estimator
          </Link>
        </div>
      </div>

      {/* Inventory load-progress banner — lives at the top so users
          see it regardless of which tab they're on. State is owned
          by Equipment() (the parent), so this MUST stay here, NOT
          inside InventoryTab where the variable is out of scope. */}
      {inventoryLoading && inventoryProgress.total > 0 && inventoryProgress.loaded < inventoryProgress.total && (
        <div className="hv-card p-3 text-xs text-amber-300 flex items-center gap-2" data-testid="inventory-loading-banner">
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
          <span>Loading inventory… <b>{inventoryProgress.loaded.toLocaleString()}</b> of <b>{inventoryProgress.total.toLocaleString()}</b> items loaded. List updates as more arrive.</span>
        </div>
      )}

      {/* Tabs — Iter 290m+o: dropped the 4 KPI hero tiles AND moved
          Scheduler + Service history into the Ops pillar (/scheduler
          + /service-history) so this page is a clean "Inventory hub". */}
      <div className="flex border border-white/10 rounded-md overflow-hidden w-fit flex-wrap">
        {[
          ["inventory", "Inventory"],
          ["procurement", "Procurement"],
          ["datasheets", "Datasheets"],
        ].map(([k, l]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            data-testid={`equip-tab-${k}`}
            className={`px-4 py-2 text-xs uppercase tracking-wider ${
              tab === k ? "bg-[#1fb6c1]/15 text-[#1fb6c1]" : "text-[#647087] hover:text-white"
            }`}
          >
            {l}
          </button>
        ))}
      </div>

      {tab === "inventory" && (
        <InventoryTab
          inventory={inventory}
          suppliers={suppliers}
          dsCounts={dsCounts}
          onAdd={(kind) => isManager && setInvModal({ initialKind: kind || "all" })}
          onEdit={(it) => isManager && setInvModal({ editing: it })}
          onDelete={async (id) => {
            if (!window.confirm("Delete this inventory item?")) return;
            await api.delete(`/inventory/${id}`);
            load();
          }}
          isManager={isManager}
          onReload={load}
        />
      )}

      {tab === "datasheets" && (
        <DatasheetsTab
          inventory={inventory}
          suppliers={suppliers}
          customers={customers}
          isManager={isManager}
        />
      )}

      {tab === "procurement" && (
        <div data-testid="procurement-tab-placeholder" className="hv-card p-6 text-center">
          <div className="text-sm text-[#a0aabf]">
            The old procurement planner has been retired. The new{" "}
            <a href="/procurement" className="text-[#22d3ee] underline">
              Procurement Tracker
            </a>{" "}
            lives at <code className="text-[#22d3ee]">/procurement</code> and
            mirrors the picking-list workflow for supplier purchase orders,
            deliveries, and GRVs.
          </div>
        </div>
      )}

      {invModal && (
        <InventoryModal
          editing={invModal.editing}
          initialKind={invModal.initialKind}
          suppliers={suppliers}
          onClose={() => setInvModal(null)}
          onSaved={() => {
            setInvModal(null);
            load();
          }}
          onOpenStock={(it) => { setInvModal(null); setStockModal(it); }}
        />
      )}
      {stockModal && (
        <StockMovementsModal
          item={stockModal}
          onClose={() => setStockModal(null)}
          onSaved={() => load()}
        />
      )}
      {eqModal && (
        <SiteEquipmentModal
          editing={eqModal.editing}
          inventory={inventory}
          customers={customers}
          sites={sites}
          onClose={() => setEqModal(null)}
          onSaved={() => {
            setEqModal(null);
            load();
          }}
        />
      )}
      {serviceModal && (
        <ServiceModal
          equipment={serviceModal.equipment}
          customers={customers}
          sites={sites}
          itemById={itemById}
          onClose={() => setServiceModal(null)}
          onSaved={() => {
            setServiceModal(null);
            load();
          }}
        />
      )}
    </div>
  );
}

function Kpi({ label, value, accent, icon: Icon, pulse }) {
  return (
    <div
      className="hv-card p-4 flex items-center justify-between"
      style={{ borderColor: `${accent}40`, background: `${accent}08` }}
    >
      <div>
        <div className="text-xs uppercase tracking-wider text-[#a0aabf]">{label}</div>
        <div className="font-display text-3xl font-bold mt-0.5" style={{ color: accent }}>
          {value}
        </div>
      </div>
      <div
        className={`w-10 h-10 rounded-md flex items-center justify-center ${pulse ? "animate-pulse" : ""}`}
        style={{ background: `${accent}1a`, color: accent, border: `1px solid ${accent}55` }}
      >
        <Icon className="w-5 h-5" />
      </div>
    </div>
  );
}

/* ---------------- Scheduler tab ---------------- */
function SchedulerTab({
  rows, customers, sites, inventory, onAdd, onEdit, onService, onDelete, isManager,
  customerName, siteName, itemById,
}) {
  const [q, setQ] = useState("");
  const [filterCustomer, setFilterCustomer] = useState("");
  const [filterSite, setFilterSite] = useState("");
  const [serviceOnly, setServiceOnly] = useState(true);

  const filtered = rows.filter((eq) => {
    if (serviceOnly && !eq.is_service_item) return false;
    if (filterCustomer && eq.customer_id !== filterCustomer) return false;
    if (filterSite && eq.site_id !== filterSite) return false;
    if (q) {
      const s = q.toLowerCase();
      if (
        !(eq.name || "").toLowerCase().includes(s) &&
        !customerName(eq.customer_id).toLowerCase().includes(s) &&
        !siteName(eq.site_id).toLowerCase().includes(s)
      )
        return false;
    }
    return true;
  });

  const customerSites = sites.filter((s) => !filterCustomer || s.customer_id === filterCustomer);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#647087]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search asset, customer, site…"
            className="hv-input hv-input-search"
            data-testid="equip-search"
          />
        </div>
        <select
          value={filterCustomer}
          onChange={(e) => {
            setFilterCustomer(e.target.value);
            setFilterSite("");
          }}
          className="hv-input py-2 text-xs w-48"
          data-testid="equip-customer-filter"
        >
          <option value="">All customers</option>
          {customers.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        <select
          value={filterSite}
          onChange={(e) => setFilterSite(e.target.value)}
          className="hv-input py-2 text-xs w-48"
        >
          <option value="">All sites</option>
          {customerSites.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
        <label className="text-xs text-[#a0aabf] inline-flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={serviceOnly}
            onChange={(e) => setServiceOnly(e.target.checked)}
            className="accent-[#1fb6c1]"
          />
          Service items only
        </label>
        <button onClick={onAdd} className="hv-btn hv-btn-primary ml-auto" data-testid="equip-new-btn">
          <Plus className="w-4 h-4" /> Add to site
        </button>
      </div>

      <div className="hv-card overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm" style={{ minWidth: 880 }}>
          <thead className="bg-[#0a0b0e] border-b border-white/10">
            <tr className="text-left text-[#647087] text-xs uppercase tracking-wider">
              <th className="px-4 py-3 whitespace-nowrap">Asset</th>
              <th className="px-4 py-3 whitespace-nowrap">Site</th>
              <th className="px-4 py-3 whitespace-nowrap">Qty</th>
              <th className="px-4 py-3 whitespace-nowrap">Interval</th>
              <th className="px-4 py-3 whitespace-nowrap">Next service</th>
              <th className="px-4 py-3 whitespace-nowrap">Last service</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((eq) => {
              const item = itemById(eq.inventory_item_id);
              const cat = CATEGORIES.find((c) => c.v === (item?.category || "general")) || CATEGORIES[6];
              const next = eq.next_service_date ? new Date(eq.next_service_date) : null;
              const overdue = next && next < new Date();
              const dueSoon = next && !overdue && next - new Date() < 7 * 24 * 3600 * 1000;
              return (
                <tr
                  key={eq.id}
                  className="border-b border-white/5 hover:bg-white/[0.03] transition"
                  data-testid={`equip-row-${eq.id}`}
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span
                        className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border font-bold"
                        style={{ borderColor: `${cat.color}55`, color: cat.color, background: `${cat.color}1a` }}
                      >
                        {cat.label}
                      </span>
                      <div>
                        <div className="font-medium">{eq.name}</div>
                        <div className="text-xs text-[#647087]">{customerName(eq.customer_id)}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-[#a0aabf]">
                    <Building2 className="w-3 h-3 inline mr-1 text-[#1fb6c1]" />
                    {siteName(eq.site_id)}
                  </td>
                  <td className="px-4 py-3 text-[#dde3ec] font-mono text-xs">
                    {eq.quantity || 1} {eq.unit || "pcs"}
                  </td>
                  <td className="px-4 py-3 text-[#a0aabf] text-xs">
                    {eq.is_service_item ? `${eq.interval_days || "—"} days` : "—"}
                  </td>
                  <td className="px-4 py-3">
                    {next ? (
                      <span
                        className="text-xs font-bold"
                        style={{
                          color: overdue ? "#F43F5E" : dueSoon ? "#F59E0B" : "#dde3ec",
                        }}
                      >
                        {next.toLocaleDateString('en-GB')}
                        {overdue && <span className="ml-1 uppercase tracking-wider text-[10px]">overdue</span>}
                      </span>
                    ) : (
                      <span className="text-[#647087] text-xs">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-[#647087] text-xs">
                    {eq.last_service_date ? new Date(eq.last_service_date).toLocaleDateString('en-GB') : "—"}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5 justify-end">
                      <button
                        onClick={() => onService(eq)}
                        className="hv-btn hv-btn-ghost text-xs"
                        data-testid={`equip-service-${eq.id}`}
                        title="Log service / replacement"
                      >
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-300" />
                      </button>
                      {isManager && (
                        <>
                          <button
                            onClick={() => onEdit(eq)}
                            className="hv-btn hv-btn-ghost text-xs"
                            data-testid={`equip-edit-${eq.id}`}
                            title="Edit"
                          >
                            <Edit3 className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => onDelete(eq.id)}
                            className="hv-btn hv-btn-ghost text-xs text-rose-300"
                            data-testid={`equip-delete-${eq.id}`}
                            title="Delete"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
        {filtered.length === 0 && (
          <div className="p-8 text-center text-[#647087] text-sm">
            No assets here yet. Click <span className="text-[#1fb6c1]">Add to site</span> to register one.
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------------- Inventory tab ---------------- */
function InventoryTab({ inventory, suppliers = [], dsCounts = {}, onAdd, onEdit, onDelete, isManager, onReload }) {
  const [q, setQ] = useState("");
  // Wave 26.9 — accept `?category=Pumps` URL param so the inventory-category
  // editor can deep-link into a pre-filtered Equipment view.
  const [searchParams, setSearchParams] = useSearchParams();
  const initialCat = searchParams.get("category") || "all";
  const [catFilter, setCatFilter] = useState(initialCat);
  useEffect(() => {
    if (initialCat !== "all" && initialCat !== catFilter) setCatFilter(initialCat);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCat]);
  // Mirror catFilter back to the URL so the back button works as expected
  // and the deep-link is shareable.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (catFilter === "all") next.delete("category");
    else next.set("category", catFilter);
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catFilter]);
  const [kind, setKind] = usePersistedState("inventory.kind", "all"); // all | equipment | component
  const [bulkOpen, setBulkOpen] = useState(false);
  const supplierName = (id) => suppliers.find((s) => s.id === id)?.name || "—";
  const filtered = inventory.filter((i) => {
    if (catFilter !== "all" && (i.category || "general") !== catFilter) return false;
    if (kind === "component" && !i.is_component && i.category !== "component") return false;
    if (kind === "equipment" && (i.is_component || i.category === "component")) return false;
    if (q) {
      const s = q.toLowerCase();
      if (
        !(i.name || "").toLowerCase().includes(s) &&
        !(i.sku || "").toLowerCase().includes(s) &&
        !(i.external_ref || "").toLowerCase().includes(s) &&
        !(i.manufacturer || "").toLowerCase().includes(s)
      )
        return false;
    }
    return true;
  });
  const equipmentCount = inventory.filter((i) => !(i.is_component || i.category === "component")).length;
  const componentCount = inventory.filter((i) => i.is_component || i.category === "component").length;
  return (
    <div className="space-y-4">
      {/* Master toggle: Equipment vs Components */}
      <div className="flex items-center gap-2 flex-wrap">
        {[
          ["all", `All (${inventory.length})`, "#1FB6C1"],
          ["equipment", `Equipment master (${equipmentCount})`, "#A78BFA"],
          ["component", `Components master (${componentCount})`, "#EAB308"],
        ].map(([k, l, c]) => (
          <button
            key={k}
            onClick={() => setKind(k)}
            data-testid={`inventory-kind-${k}`}
            className={`px-3 py-1.5 rounded-full border text-xs uppercase tracking-wider font-bold transition ${
              kind === k ? "" : "text-[#647087] border-white/10 hover:text-white"
            }`}
            style={kind === k ? { borderColor: `${c}55`, background: `${c}1a`, color: c } : {}}
          >
            {l}
          </button>
        ))}
      </div>
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#647087]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, SKU, ref, manufacturer…"
            className="hv-input hv-input-search"
            data-testid="inventory-search"
          />
        </div>
        <select
          value={catFilter}
          onChange={(e) => setCatFilter(e.target.value)}
          className="hv-input py-2 text-xs w-48"
        >
          <option value="all">All categories</option>
          {CATEGORIES.map((c) => (
            <option key={c.v} value={c.v}>
              {c.label}
            </option>
          ))}
        </select>
        {isManager && (
          <>
            <Link
              to="/tools/inventory-categories"
              className="hv-btn hv-btn-ghost text-xs"
              title="Re-categorize items (catalogue manager)"
              data-testid="inventory-catalog-btn"
            >
              <Tags className="w-4 h-4" /> Catalogue
            </Link>
            <button
              onClick={() => setBulkOpen(true)}
              className="hv-btn hv-btn-ghost text-xs"
              title="Import inventory from CSV / XLSX / PDF / image"
              data-testid="inventory-bulk-import-btn"
            >
              <Upload className="w-4 h-4" /> Import
            </button>
            <InventoryExportMenu
              q={q}
              category={catFilter !== "all" ? catFilter : undefined}
              kind={kind}
            />
            <button
              onClick={async () => {
                if (!window.confirm("Walk every inventory row that has a supplier code (e.g. 'ACDC001') and link it to its matching supplier record. Safe to re-run any time. Continue?")) return;
                try {
                  const { data } = await api.post("/inventory/resolve-supplier-codes");
                  const total = data.total_inventory_with_supplier_id || 0;
                  if (data.no_supplier_codes_defined) {
                    toast.error("No supplier codes defined yet. Add a few suppliers with their accounting code first.");
                    return;
                  }
                  toast.success(`${total.toLocaleString()} item(s) now linked to suppliers. ${data.unresolved_count} code(s) still unmatched.`);
                  onReload?.();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Resolver failed.");
                }
              }}
              className="hv-btn hv-btn-ghost text-xs"
              title="Auto-link every inventory row to its supplier by matching supplier code"
              data-testid="inventory-resolve-suppliers"
            >
              <Building2 className="w-4 h-4" /> Link suppliers
            </button>
            <button onClick={() => onAdd(kind)} className="hv-btn hv-btn-primary ml-auto" data-testid="inventory-new-btn">
              <Plus className="w-4 h-4" /> New {kind === "component" ? "component" : "item"}
            </button>
          </>
        )}
      </div>

      <CompactInventoryTable
        items={filtered}
        suppliers={suppliers}
        dsCounts={dsCounts}
        onOpen={(i) => onEdit(i)}
        isManager={isManager}
        onDelete={onDelete}
        onReload={onReload}
      />

      {bulkOpen && (
        <BulkImportModal
          kind="inventory"
          onClose={() => setBulkOpen(false)}
          onCommitted={() => {
            setBulkOpen(false);
            onReload?.();
          }}
        />
      )}
    </div>
  );
}

/* ---------------- Inventory export menu ----------------
 * Tiny dropdown that drops a CSV/XLSX in the user's downloads folder.
 * Calls the backend export endpoint with the current filter state so
 * "Export visible" matches what the table shows.
 */
function InventoryExportMenu({ q, category, kind }) {
  const [open, setOpen] = useState(false);
  const trigger = async (format, ext) => {
    setOpen(false);
    try {
      const params = new URLSearchParams();
      params.set("format", format);
      params.set("ext", ext);
      if (q) params.set("q", q);
      if (category) params.set("category", category);
      if (kind && kind !== "all") params.set("kind", kind);
      const r = await api.get(`/inventory/export?${params.toString()}`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: r.headers["content-type"] || "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const cd = r.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      a.download = m ? m[1] : `inventory-${format}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${a.download}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Export failed.");
    }
  };
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="hv-btn hv-btn-ghost text-xs"
        title="Download inventory as CSV / Excel"
        data-testid="inventory-export-btn"
      >
        <Download className="w-4 h-4" /> Export
      </button>
      {open && (
        <div className="absolute z-30 right-0 mt-1 w-72 hv-card p-2 space-y-1" data-testid="inventory-export-menu">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087] px-2 pt-1">Accounting (IQ Retail / Pastel)</div>
          <button onClick={() => trigger("accounting", "csv")} className="hv-btn hv-btn-ghost text-xs w-full justify-start" data-testid="inventory-export-acc-csv">
            <Download className="w-3.5 h-3.5" /> Download accounting CSV
          </button>
          <button onClick={() => trigger("accounting", "xlsx")} className="hv-btn hv-btn-ghost text-xs w-full justify-start" data-testid="inventory-export-acc-xlsx">
            <Download className="w-3.5 h-3.5" /> Download accounting Excel
          </button>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087] px-2 pt-2">Internal (every field)</div>
          <button onClick={() => trigger("hidroverse", "csv")} className="hv-btn hv-btn-ghost text-xs w-full justify-start" data-testid="inventory-export-hv-csv">
            <Download className="w-3.5 h-3.5" /> Download full CSV
          </button>
          <button onClick={() => trigger("hidroverse", "xlsx")} className="hv-btn hv-btn-ghost text-xs w-full justify-start" data-testid="inventory-export-hv-xlsx">
            <Download className="w-3.5 h-3.5" /> Download full Excel
          </button>
          <div className="px-2 pt-1 pb-1 text-[10px] text-[#647087]">
            Honours your current search + category + kind filters.
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------- Compact inventory price-list table ----------------
 * Single source of truth for what the Inventory tab looks like. Renders
 * up to 500 rows at a time (the full filtered set is sliced) so a 17 k
 * collection doesn't bog the DOM. "Load more" appends the next page.
 *
 * Columns: SKU · Description · Category · Supplier · On hand · Cost · Selling
 * Clicking a row → opens the existing InventoryModal in edit mode (the
 * "full detail" view).
 */
const PAGE_SIZE = 500;

function CompactInventoryTable({ items, suppliers, dsCounts, onOpen, isManager, onDelete, onReload }) {
  const [shown, setShown] = useState(PAGE_SIZE);
  // Dimension labels (`{labels: {category:{code→label}, department:{code→label}}, codes:[...]}`).
  // Loaded once and re-fetched after the user edits a label.
  const [dimLabels, setDimLabels] = useState({ category: {}, department: {} });
  // Inline-edit state for the cat/dept badge. `{ kind, code, value }`.
  const [editingLabel, setEditingLabel] = useState(null);
  const [savingLabel, setSavingLabel] = useState(false);

  const loadLabels = async () => {
    try {
      const { data } = await api.get("/inventory/dimension-labels");
      setDimLabels(data?.labels || { category: {}, department: {} });
    } catch { /* noop */ }
  };
  useEffect(() => { loadLabels(); }, []);
  // Reset paging when filters change.
  useEffect(() => { setShown(PAGE_SIZE); }, [items.length]);

  const saveLabel = async () => {
    if (!editingLabel) return;
    setSavingLabel(true);
    try {
      await api.put("/inventory/dimension-labels", {
        kind: editingLabel.kind,
        code: editingLabel.code,
        label: (editingLabel.value || "").trim(),
      });
      setEditingLabel(null);
      await loadLabels();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save label.");
    } finally { setSavingLabel(false); }
  };

  const renderDim = (code, kind) => {
    if (!code) return <span className="text-[#647087]">—</span>;
    const lbl = dimLabels[kind]?.[String(code)];
    if (editingLabel?.kind === kind && editingLabel?.code === String(code)) {
      return (
        <input
          autoFocus
          value={editingLabel.value}
          onChange={(e) => setEditingLabel({ ...editingLabel, value: e.target.value })}
          onClick={(e) => e.stopPropagation()}
          onBlur={saveLabel}
          onKeyDown={(e) => {
            if (e.key === "Enter") saveLabel();
            if (e.key === "Escape") setEditingLabel(null);
          }}
          placeholder={`name for ${kind} ${code}`}
          className="hv-input text-[10px] py-0.5 px-1 w-24"
          disabled={savingLabel}
        />
      );
    }
    return (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          if (isManager) setEditingLabel({ kind, code: String(code), value: lbl || "" });
        }}
        className={"text-[10px] " + (isManager ? "hover:text-[#1FB6C1] cursor-text" : "cursor-default") + " " + (lbl ? "text-[#dde3ec]" : "text-amber-300")}
        title={isManager ? (lbl ? `Click to rename ${kind} ${code}` : `Click to name ${kind} ${code}`) : ""}
      >
        {lbl || `#${code}`}
      </button>
    );
  };

  const supplierLabel = (i) => {
    const id = i.supplier_id;
    if (id) {
      const s = suppliers.find((x) => x.id === id);
      if (s) return s.name || s.code || id;
    }
    return i.supplier_1 || i.supplier_code || "—";
  };

  const fmtZar = (v) => Number.isFinite(v) ? `R ${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "—";
  const fmtPct = (v) => Number.isFinite(v) ? `${v.toFixed(1)}%` : "—";

  const slice = items.slice(0, shown);
  const moreCount = Math.max(0, items.length - shown);

  if (items.length === 0) {
    return (
      <div className="hv-card p-8 text-center text-[#647087] text-sm" data-testid="inventory-empty">
        No inventory items match these filters.
      </div>
    );
  }

  return (
    <div className="hv-card overflow-hidden" data-testid="inventory-pricelist">
      <div className="overflow-hidden" style={{ width: '100%' }}>
        <table
          className="hv-table-keep text-[11px]"
          style={{ tableLayout: 'fixed', width: '100%' }}
        >
          <thead className="text-left sticky top-0 bg-[#0a0b0e] z-[1]">
            <tr className="text-[10px] uppercase tracking-[0.15em] text-[#647087] bg-white/[0.02]">
              <th style={{ width: 68 }} className="px-1.5 py-1 whitespace-nowrap">SKU</th>
              <th className="px-1.5 py-1 whitespace-nowrap">Description</th>
              <th style={{ width: 42 }} className="px-1.5 py-1 text-right whitespace-nowrap" title="Quantity on hand">QTY</th>
              <th style={{ width: 68 }} className="px-1.5 py-1 text-right whitespace-nowrap">Sell</th>
              <th style={{ width: 96 }} className="px-1.5 py-1 whitespace-nowrap hidden sm:table-cell">Cat</th>
              <th style={{ width: 112 }} className="px-1.5 py-1 whitespace-nowrap hidden md:table-cell">Dept</th>
              <th style={{ width: 112 }} className="px-1.5 py-1 whitespace-nowrap hidden sm:table-cell">Supplier</th>
              <th style={{ width: 80 }} className="px-1.5 py-1 text-right whitespace-nowrap hidden sm:table-cell">Cost</th>
              <th style={{ width: 80 }} className="px-1.5 py-1 text-right whitespace-nowrap hidden lg:table-cell">Profit</th>
              <th style={{ width: 48 }} className="px-1.5 py-1 text-right whitespace-nowrap hidden lg:table-cell">GP%</th>
              <th style={{ width: 56 }} className="px-1.5 py-1 text-right whitespace-nowrap hidden lg:table-cell">Markup</th>
              {isManager && <th style={{ width: 28 }} className="px-1 py-1"></th>}
            </tr>
          </thead>
          <tbody>
            {slice.map((i) => {
              const isComp = i.is_component || i.category === "component";
              const onHand = Number(i.on_hand_qty);
              const cost = Number(i.avg_cost);
              const sell = Number(i.sell_price_incl ?? i.sell_price_excl);
              const profit = (Number.isFinite(sell) && Number.isFinite(cost)) ? sell - cost : NaN;
              const gp = (Number.isFinite(profit) && sell > 0) ? (profit / sell) * 100 : NaN;
              const markup = (Number.isFinite(profit) && cost > 0) ? (profit / cost) * 100 : NaN;
              const lowStock = i.reorder_level && onHand <= Number(i.reorder_level);
              return (
                <tr
                  key={i.id}
                  className="border-t border-white/5 hover:bg-white/[0.03] cursor-pointer transition-colors"
                  onClick={() => onOpen(i)}
                  data-testid={`inventory-row-${i.id}`}
                >
                  <td className="px-1.5 py-1 font-mono text-[10px] text-[#9aa3b5] whitespace-nowrap overflow-hidden">
                    <div className="truncate" title={i.sku || i.barcode || i.external_ref || ""}>
                      {i.sku || i.barcode || i.external_ref || "—"}
                    </div>
                  </td>
                  <td className="px-1.5 py-1 text-white">
                    <div className="truncate" title={i.name}>{i.name || "—"}</div>
                    {(dsCounts[i.id] > 0 || isComp) && (
                      <div className="flex items-center gap-2 leading-none">
                        {dsCounts[i.id] > 0 && (
                          <span className="text-[9px] text-[#1FB6C1] inline-flex items-center gap-0.5">
                            <FileText className="w-2.5 h-2.5" /> {dsCounts[i.id]}
                          </span>
                        )}
                        {isComp && <span className="text-[9px] text-amber-300">· component</span>}
                      </div>
                    )}
                  </td>
                  <td className={"px-1.5 py-1 text-right tabular-nums whitespace-nowrap " + (lowStock ? "text-rose-300 font-bold" : "text-[#dde3ec]")}>
                    {Number.isFinite(onHand) ? onHand.toLocaleString(undefined, { maximumFractionDigits: 0 }) : "—"}
                  </td>
                  <td className="px-1.5 py-1 text-right tabular-nums text-emerald-300 font-medium whitespace-nowrap">{fmtZar(sell)}</td>
                  <td className="px-1.5 py-1 whitespace-nowrap hidden sm:table-cell">{renderDim(i.category, "category")}</td>
                  <td className="px-1.5 py-1 whitespace-nowrap hidden md:table-cell">{renderDim(i.department, "department")}</td>
                  <td className="px-1.5 py-1 text-[#9aa3b5] hidden sm:table-cell" title={supplierLabel(i)}>
                    <div className="truncate max-w-[112px]">{supplierLabel(i)}</div>
                  </td>
                  <td className="px-1.5 py-1 text-right tabular-nums text-[#9aa3b5] whitespace-nowrap hidden sm:table-cell">{fmtZar(cost)}</td>
                  <td className={"px-1.5 py-1 text-right tabular-nums whitespace-nowrap hidden lg:table-cell " + (Number.isFinite(profit) ? (profit >= 0 ? "text-emerald-300" : "text-rose-300") : "text-[#647087]")}>
                    {fmtZar(profit)}
                  </td>
                  <td className={"px-1.5 py-1 text-right tabular-nums whitespace-nowrap hidden lg:table-cell " + (Number.isFinite(gp) ? (gp >= 25 ? "text-emerald-300" : gp >= 10 ? "text-amber-300" : "text-rose-300") : "text-[#647087]")}>
                    {fmtPct(gp)}
                  </td>
                  <td className="px-1.5 py-1 text-right tabular-nums text-[#9aa3b5] whitespace-nowrap hidden lg:table-cell">{fmtPct(markup)}</td>
                  {isManager && (
                    <td className="px-1 py-1">
                      <button
                        onClick={(ev) => { ev.stopPropagation(); onDelete(i.id); }}
                        className="text-[#647087] hover:text-rose-300 transition-colors"
                        title="Delete"
                        data-testid={`inventory-delete-${i.id}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {moreCount > 0 && (
        <div className="border-t border-white/5 p-3 text-center">
          <button
            type="button"
            onClick={() => setShown((s) => s + PAGE_SIZE)}
            className="hv-btn hv-btn-ghost text-xs"
            data-testid="inventory-load-more"
          >
            Load {Math.min(moreCount, PAGE_SIZE).toLocaleString()} more ({moreCount.toLocaleString()} remaining)
          </button>
        </div>
      )}
      <div className="border-t border-white/5 px-3 py-1.5 text-[10px] text-[#647087] flex items-center justify-between flex-wrap gap-2">
        <span>Showing {slice.length.toLocaleString()} of {items.length.toLocaleString()}</span>
        <span>
          Click row → full detail · {isManager ? "Click a Cat/Dept code to name it" : "Currency in ZAR"} · GP = Profit ÷ Sell · Markup = Profit ÷ Cost
        </span>
      </div>
    </div>
  );
}

/* ---------------- History tab ---------------- */
function HistoryTab({ logs, customers, sites, inventory, customerName, siteName, itemById }) {
  const [q, setQ] = useState("");
  const [filterCustomer, setFilterCustomer] = useState("");
  const [filterSite, setFilterSite] = useState("");

  const filtered = logs
    .filter((l) => {
      if (filterCustomer && l.customer_id !== filterCustomer) return false;
      if (filterSite && l.site_id !== filterSite) return false;
      if (q) {
        const s = q.toLowerCase();
        if (
          !(l.equipment_name || "").toLowerCase().includes(s) &&
          !customerName(l.customer_id).toLowerCase().includes(s) &&
          !siteName(l.site_id).toLowerCase().includes(s) &&
          !(l.notes || "").toLowerCase().includes(s)
        )
          return false;
      }
      return true;
    })
    .sort((a, b) => new Date(b.service_date || b.created_at) - new Date(a.service_date || a.created_at));

  const customerSites = sites.filter((s) => !filterCustomer || s.customer_id === filterCustomer);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#647087]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search asset, customer, site, notes…"
            className="hv-input hv-input-search"
            data-testid="history-search"
          />
        </div>
        <select
          value={filterCustomer}
          onChange={(e) => {
            setFilterCustomer(e.target.value);
            setFilterSite("");
          }}
          className="hv-input py-2 text-xs w-48"
        >
          <option value="">All customers</option>
          {customers.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        <select
          value={filterSite}
          onChange={(e) => setFilterSite(e.target.value)}
          className="hv-input py-2 text-xs w-48"
        >
          <option value="">All sites</option>
          {customerSites.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
      </div>

      <div className="hv-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#0a0b0e] border-b border-white/10">
            <tr className="text-left text-[#647087] text-xs uppercase tracking-wider">
              <th className="px-4 py-3">Date</th>
              <th className="px-4 py-3">Asset</th>
              <th className="px-4 py-3">Action</th>
              <th className="px-4 py-3">Qty</th>
              <th className="px-4 py-3">Site</th>
              <th className="px-4 py-3">Notes</th>
              <th className="px-4 py-3">By</th>
              <th className="px-4 py-3">Job card</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((l) => (
              <tr
                key={l.id}
                className="border-b border-white/5 hover:bg-white/[0.03] transition"
                data-testid={`history-row-${l.id}`}
              >
                <td className="px-4 py-3 text-[#dde3ec] whitespace-nowrap text-xs">
                  {new Date(l.service_date || l.created_at).toLocaleDateString('en-GB')}
                </td>
                <td className="px-4 py-3">
                  <div className="font-medium">{l.equipment_name || "—"}</div>
                  <div className="text-xs text-[#647087]">{customerName(l.customer_id)}</div>
                </td>
                <td className="px-4 py-3">
                  <span
                    className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border font-bold"
                    style={{
                      borderColor:
                        l.action === "replaced"
                          ? "#F59E0B66"
                          : l.action === "installed"
                          ? "#3B82F666"
                          : "#10B98166",
                      color:
                        l.action === "replaced"
                          ? "#F59E0B"
                          : l.action === "installed"
                          ? "#3B82F6"
                          : "#10B981",
                      background:
                        l.action === "replaced"
                          ? "#F59E0B1a"
                          : l.action === "installed"
                          ? "#3B82F61a"
                          : "#10B9811a",
                    }}
                  >
                    {l.action}
                  </span>
                </td>
                <td className="px-4 py-3 font-mono text-xs">
                  {l.quantity ? `${l.quantity} ${l.unit || "pcs"}` : "—"}
                </td>
                <td className="px-4 py-3 text-[#a0aabf] text-xs">{siteName(l.site_id)}</td>
                <td className="px-4 py-3 text-[#a0aabf] text-xs max-w-md">
                  <div className="line-clamp-2 whitespace-pre-wrap">{l.notes || "—"}</div>
                </td>
                <td className="px-4 py-3 text-[#dde3ec] text-xs">{l.by_user_name || "—"}</td>
                <td className="px-4 py-3 text-xs">
                  {l.jobcard_id ? (
                    /* Iter 427.p48 (WS2) — direct JC link from
                       service history for fast traceability. */
                    <Link
                      to={`/jobcards/${l.jobcard_id}`}
                      className="inline-flex items-center gap-1 text-[#1fb6c1] hover:text-[#5fe3ec] hover:underline"
                      data-testid={`history-jc-link-${l.id}`}
                      title="Open job card"
                    >
                      View <ArrowRight className="w-3 h-3" />
                    </Link>
                  ) : (
                    <span className="text-[#647087]">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <div className="p-8 text-center text-[#647087] text-sm">
            No service history yet.
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------------- Inventory modal ---------------- */
function InventoryModal({ editing, initialKind, suppliers = [], onClose, onSaved, onOpenStock }) {
  const [form, setForm] = useState(
    editing || {
      name: "",
      category: initialKind === "component" ? "component" : "general",
      sku: "",
      unit: "pcs",
      is_service_item: initialKind === "component",
      default_interval_days: 90,
      default_quantity: 1,
      notes: "",
      external_ref: "",
      description: "",
      manufacturer: "",
      is_component: initialKind === "component",
      supplier_id: "",
      location: "",
    }
  );
  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      default_quantity: form.default_quantity ? Number(form.default_quantity) : 1,
      default_interval_days: form.is_service_item
        ? form.default_interval_days
          ? Number(form.default_interval_days)
          : null
        : null,
    };
    if (editing?.id) await api.put(`/inventory/${editing.id}`, payload);
    else await api.post("/inventory", payload);
    onSaved();
  };
  return (
    <Modal open onClose={onClose} title={editing?.id ? "Edit item" : (form.is_component ? "New component" : "New inventory item")} testId="inventory-modal" maxWidth="max-w-xl">
      <form onSubmit={submit} className="space-y-3" data-testid="inventory-form">
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2">
            <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Name *</label>
            <input
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="hv-input mt-1"
              data-testid="inventory-input-name"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Category</label>
            <select
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              className="hv-input mt-1"
            >
              {CATEGORIES.map((c) => (
                <option key={c.v} value={c.v}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#a0aabf]">SKU</label>
            <input
              value={form.sku}
              onChange={(e) => setForm({ ...form, sku: e.target.value })}
              className="hv-input mt-1"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#a0aabf]">External Ref / AppSheet ID</label>
            <input
              value={form.external_ref || ""}
              onChange={(e) => setForm({ ...form, external_ref: e.target.value })}
              className="hv-input mt-1 font-mono text-xs"
              data-testid="inventory-input-external-ref"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Manufacturer</label>
            <input
              value={form.manufacturer || ""}
              onChange={(e) => setForm({ ...form, manufacturer: e.target.value })}
              className="hv-input mt-1"
              data-testid="inventory-input-manufacturer"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Supplier</label>
            <select
              value={form.supplier_id || ""}
              onChange={(e) => setForm({ ...form, supplier_id: e.target.value })}
              className="hv-input mt-1"
              data-testid="inventory-input-supplier"
            >
              <option value="">— None —</option>
              {suppliers.map((s) => (
                <option key={s.id} value={s.id}>{s.name}{s.external_ref ? ` · ${s.external_ref}` : ""}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Storage location</label>
            <input
              value={form.location || ""}
              onChange={(e) => setForm({ ...form, location: e.target.value })}
              className="hv-input mt-1"
              placeholder="e.g. Warehouse · Shelf B-3"
              data-testid="inventory-input-location"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Unit</label>
            <select
              value={form.unit}
              onChange={(e) => setForm({ ...form, unit: e.target.value })}
              className="hv-input mt-1"
            >
              {UNITS.map((u) => (
                <option key={u} value={u}>
                  {u}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Default qty</label>
            <input
              type="number"
              step="0.1"
              value={form.default_quantity || ""}
              onChange={(e) => setForm({ ...form, default_quantity: e.target.value })}
              className="hv-input mt-1"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Description</label>
            <VoiceTextarea
              value={form.description || ""}
              onChange={(v) => setForm({ ...form, description: v })}
              className="mt-1 min-h-[60px]"
              context="inventory_description"
              testIdPrefix="inv-desc"
              data-testid="inventory-input-description"
            />
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm cursor-pointer mt-2">
          <input
            type="checkbox"
            checked={!!form.is_component}
            onChange={(e) => setForm({ ...form, is_component: e.target.checked })}
            className="accent-[#EAB308]"
            data-testid="inventory-input-is-component"
          />
          <span>Treat as a <span className="text-[#EAB308] font-bold">component</span> (vs. equipment)</span>
        </label>
        <label className="flex items-center gap-2 text-sm cursor-pointer mt-2">
          <input
            type="checkbox"
            checked={form.is_service_item}
            onChange={(e) => setForm({ ...form, is_service_item: e.target.checked })}
            className="accent-[#1fb6c1]"
            data-testid="inventory-input-service"
          />
          <span>This is a service item (recurring service / replacement)</span>
        </label>
        {form.is_service_item && (
          <div>
            <label className="text-xs uppercase tracking-wider text-[#a0aabf]">
              Default service interval (days)
            </label>
            <input
              type="number"
              value={form.default_interval_days || ""}
              onChange={(e) => setForm({ ...form, default_interval_days: e.target.value })}
              className="hv-input mt-1"
              data-testid="inventory-input-interval"
            />
            <div className="text-[10px] text-[#647087] mt-1">
              Auto-fills the per-site interval; can be overridden per site.
            </div>
          </div>
        )}
        <div>
          <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Notes</label>
          <VoiceTextarea
            value={form.notes}
            onChange={(v) => setForm({ ...form, notes: v })}
            className="mt-1 min-h-[60px]"
            context="equipment_notes"
            testIdPrefix="equip-notes"
          />
        </div>
        <div className="flex items-center gap-2">
          <button type="submit" className="hv-btn hv-btn-primary flex-1" data-testid="inventory-submit">
            {editing?.id ? "Save changes" : "Create item"}
          </button>
          {editing?.id && onOpenStock && (
            <button
              type="button"
              onClick={() => onOpenStock(editing)}
              className="hv-btn hv-btn-ghost text-xs"
              data-testid="inventory-open-stock-history"
              title="View stock movements and adjust on-hand quantity"
            >
              <History className="w-3.5 h-3.5" /> Stock history
            </button>
          )}
        </div>
      </form>
    </Modal>
  );
}

/* ---------------- Site equipment modal (add/edit) ---------------- */
function SiteEquipmentModal({ editing, inventory, customers, sites, onClose, onSaved }) {
  const [form, setForm] = useState(
    editing || {
      customer_id: "",
      site_id: "",
      inventory_item_id: "",
      name: "",
      quantity: 1,
      unit: "pcs",
      is_service_item: false,
      interval_days: "",
      next_service_date: "",
      install_date: "",
      notes: "",
    }
  );
  const customerSites = sites.filter((s) => s.customer_id === form.customer_id);
  const selectedItem = inventory.find((i) => i.id === form.inventory_item_id);

  // when picking an inventory item, auto-fill defaults
  const pickItem = (id) => {
    const item = inventory.find((i) => i.id === id);
    if (!item) return setForm({ ...form, inventory_item_id: "" });
    setForm({
      ...form,
      inventory_item_id: id,
      name: form.name || item.name,
      quantity: form.quantity || item.default_quantity || 1,
      unit: item.unit || "pcs",
      is_service_item: item.is_service_item || false,
      interval_days: item.is_service_item ? form.interval_days || item.default_interval_days : "",
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      quantity: Number(form.quantity || 1),
      interval_days: form.is_service_item && form.interval_days ? Number(form.interval_days) : null,
      next_service_date: form.next_service_date || null,
      install_date: form.install_date || null,
    };
    if (editing?.id) await api.put(`/equipment/${editing.id}`, payload);
    else await api.post("/equipment", payload);
    onSaved();
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={editing?.id ? "Edit asset" : "Add asset to site"}
      testId="site-equipment-modal"
      maxWidth="max-w-2xl"
    >
      <form onSubmit={submit} className="space-y-3" data-testid="site-equipment-form">
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Customer *</label>
            <SearchableSelect
              value={form.customer_id || ""}
              onChange={(id) => setForm({ ...form, customer_id: id, site_id: "" })}
              options={customers.map((c) => ({
                id: c.id,
                label: c.name,
                sublabel: [c.external_ref && `Acc# ${c.external_ref}`, c.vat_number && `VAT ${c.vat_number}`, c.phone, c.email].filter(Boolean).join(" · "),
                searchTerms: [c.name, c.external_ref, c.vat_number, c.phone, c.whatsapp, c.email, c.industry, c.physical_address, c.legal_name, c.short_id].filter(Boolean),
              }))}
              placeholder="Select…"
              required
              disabled={!!editing?.id}
              testId="site-equip-customer"
              emptyHint="No customers match. Try VAT, account no., phone or email."
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Site *</label>
            <SearchableSelect
              value={form.site_id || ""}
              onChange={(id) => setForm({ ...form, site_id: id })}
              options={customerSites.map((s) => ({
                id: s.id,
                label: s.name,
                sublabel: s.address || "",
                searchTerms: [s.name, s.address, s.external_ref].filter(Boolean),
              }))}
              placeholder="Select…"
              required
              disabled={!!editing?.id}
              testId="site-equip-site"
            />
          </div>
        </div>

        <div>
          <label className="text-xs uppercase tracking-wider text-[#a0aabf]">From inventory</label>
          <select
            value={form.inventory_item_id}
            onChange={(e) => pickItem(e.target.value)}
            className="hv-input mt-1"
            data-testid="site-equip-inventory"
          >
            <option value="">— Custom (no inventory item) —</option>
            {inventory.map((i) => (
              <option key={i.id} value={i.id}>
                {i.name}
                {i.sku ? ` · ${i.sku}` : ""} {i.is_service_item ? "· (service)" : ""}
              </option>
            ))}
          </select>
        </div>

        <div className="grid sm:grid-cols-3 gap-3">
          <div className="sm:col-span-2">
            <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Asset name *</label>
            <input
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="hv-input mt-1"
              placeholder={selectedItem?.name || "e.g. RO membrane stage 1"}
              data-testid="site-equip-name"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#a0aabf]">
              Quantity ({form.unit || "pcs"})
            </label>
            <input
              type="number"
              step="0.1"
              value={form.quantity}
              onChange={(e) => setForm({ ...form, quantity: e.target.value })}
              className="hv-input mt-1"
              data-testid="site-equip-qty"
            />
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={form.is_service_item}
            onChange={(e) => setForm({ ...form, is_service_item: e.target.checked })}
            className="accent-[#1fb6c1]"
          />
          <span>Recurring service tracked for this asset</span>
        </label>

        {form.is_service_item && (
          <div className="grid sm:grid-cols-2 gap-3 border border-[#1fb6c1]/20 rounded-md p-3 bg-[#1fb6c1]/5">
            <div>
              <label className="text-xs uppercase tracking-wider text-[#a0aabf]">
                Interval (days) — overrides default
              </label>
              <input
                type="number"
                value={form.interval_days}
                onChange={(e) => setForm({ ...form, interval_days: e.target.value })}
                className="hv-input mt-1"
                placeholder={selectedItem?.default_interval_days?.toString() || "e.g. 90"}
                data-testid="site-equip-interval"
              />
              <div className="text-[10px] text-[#647087] mt-1">
                Future services for this site will use this interval.
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Next service date</label>
              <DateTimePicker
                value={form.next_service_date}
                onChange={(v) => setForm({ ...form, next_service_date: v })}
                placeholder="Pick next service date"
                showTime={false}
                className="mt-1"
              />
            </div>
          </div>
        )}

        <div>
          <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Install date</label>
          <DateTimePicker
            value={form.install_date}
            onChange={(v) => setForm({ ...form, install_date: v })}
            placeholder="Pick install date"
            showTime={false}
            className="mt-1"
          />
        </div>

        <div>
          <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Notes</label>
          <VoiceTextarea
            value={form.notes}
            onChange={(v) => setForm({ ...form, notes: v })}
            className="mt-1 min-h-[60px]"
            context="equipment_notes"
            testIdPrefix="equip-notes"
          />
        </div>

        <button type="submit" className="hv-btn hv-btn-primary w-full" data-testid="site-equip-submit">
          {editing?.id ? "Save changes" : "Add asset"}
        </button>
      </form>
    </Modal>
  );
}

/* ---------------- Service log modal ---------------- */
function ServiceModal({ equipment: eq, customers, sites, itemById, onClose, onSaved }) {
  const item = itemById(eq.inventory_item_id);
  const [form, setForm] = useState({
    action: "serviced",
    quantity: eq.quantity || 1,
    notes: "",
    service_date: new Date().toISOString().slice(0, 19),
    create_jobcard: false,
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      let jobcardId = null;
      if (form.create_jobcard) {
        const jc = await api.post("/jobcards", {
          customer_id: eq.customer_id,
          site_id: eq.site_id,
          title: `[Service] ${eq.name}`,
          description: form.notes || `Service log for ${eq.name}`,
          phase: "complete",
          scheduled_at: form.service_date,
          priority: "normal",
        });
        jobcardId = jc.data.id;
      }
      await api.post(`/equipment/${eq.id}/service`, {
        action: form.action,
        quantity: Number(form.quantity),
        service_date: form.service_date,
        notes: form.notes,
        jobcard_id: jobcardId,
      });
      onSaved();
    } finally {
      setSaving(false);
    }
  };

  const cust = customers.find((c) => c.id === eq.customer_id);
  const site = sites.find((s) => s.id === eq.site_id);

  return (
    <Modal open onClose={onClose} title={`Log service · ${eq.name}`} testId="service-modal" maxWidth="max-w-xl">
      <form onSubmit={submit} className="space-y-3" data-testid="service-form">
        <div className="text-xs text-[#a0aabf]">
          {cust?.name} · <span className="text-[#1fb6c1]">{site?.name}</span>
          {item && (
            <span>
              {" "}
              · <span className="text-[#a78bfa]">{item.name}</span>
            </span>
          )}
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Action *</label>
            <select
              value={form.action}
              onChange={(e) => setForm({ ...form, action: e.target.value })}
              className="hv-input mt-1"
              data-testid="service-action"
            >
              <option value="serviced">Serviced</option>
              <option value="replaced">Replaced</option>
              <option value="inspected">Inspected</option>
              <option value="installed">Installed</option>
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#a0aabf]">
              Quantity ({eq.unit || "pcs"})
            </label>
            <input
              type="number"
              step="0.1"
              value={form.quantity}
              onChange={(e) => setForm({ ...form, quantity: e.target.value })}
              className="hv-input mt-1"
              data-testid="service-qty"
            />
          </div>
        </div>

        <div>
          <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Service date</label>
          <DateTimePicker
            value={form.service_date}
            onChange={(v) => setForm({ ...form, service_date: v })}
            placeholder="Pick date & time"
            className="mt-1"
            testId="service-date"
          />
        </div>

        <div>
          <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Notes</label>
          <VoiceTextarea
            value={form.notes}
            onChange={(v) => setForm({ ...form, notes: v })}
            className="mt-1 min-h-[100px]"
            placeholder="What was done? Parts used, observations…"
            context="equipment_service_notes"
            testIdPrefix="service-notes"
            data-testid="service-notes"
          />
        </div>

        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={form.create_jobcard}
            onChange={(e) => setForm({ ...form, create_jobcard: e.target.checked })}
            className="accent-[#1fb6c1]"
            data-testid="service-create-jc"
          />
          <span>Also create a completed Job Card for this service</span>
        </label>

        {eq.is_service_item && eq.interval_days && (
          <div className="text-[10px] text-emerald-300/80 inline-flex items-center gap-1">
            <ArrowRight className="w-3 h-3" /> Next service auto-scheduled for{" "}
            {new Date(
              new Date(form.service_date).getTime() + eq.interval_days * 24 * 3600 * 1000
            ).toLocaleDateString('en-GB')}
          </div>
        )}

        <button
          type="submit"
          className="hv-btn hv-btn-primary w-full"
          disabled={saving}
          data-testid="service-submit"
        >
          <CheckCircle2 className="w-4 h-4" /> {saving ? "Saving…" : "Log service"}
        </button>
      </form>
    </Modal>
  );
}


/* ---------------- Procurement tab — moved to /procurement (Iter 402.0) ---------------- */
// The full ProcurementTab component + ProcurementPlanner embed were removed
// when we killed the old procurement planner. The tab still exists as an
// info card that redirects users to the new Procurement Tracker page.
